import { Modal, VerticalContainer, Skeleton, Paragraph } from '@/components';
import { useAtomValue } from 'jotai';
import { insurancePolicyProviderDetailsQueryAtom } from '../store/store';
import {
    CaregiverRoleEnum,
    getEnumTranslationByKey,
    InsurancePolicyProviderType,
    ReferralOptionEnum,
} from '@/shared/enums';
import { InsurancePolicyProviderDetailsDto } from '../types/InsurancePolicyProviderDetailsDto';
import { format, R } from '../resources/Texts.resx';

type InsurancePolicyProviderDetailsModalProps = {
    insurancePolicyProviderType: InsurancePolicyProviderType;
    onClose: () => void;
};

export const InsurancePolicyProviderDetailsModal = ({
    insurancePolicyProviderType,
    onClose,
}: InsurancePolicyProviderDetailsModalProps) => {
    const { data, isPending } = useAtomValue(
        insurancePolicyProviderDetailsQueryAtom(insurancePolicyProviderType),
    );

    return (
        <Modal title={getTitle(data, isPending)} onClose={onClose}>
            {(isPending || !data) && (
                <VerticalContainer spacing="sm">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                </VerticalContainer>
            )}
            {data && (
                <VerticalContainer spacing="md">
                    <Paragraph
                        content={format(
                            R.InsuranceProviderFreeAccess,
                            data.Name,
                        )}
                        className="m-0 text-sm font-normal text-gray-900"
                    />
                    <ul className="list-disc pl-8 text-gray-900">
                        {data?.CaregiverRoles.map((role) => (
                            <li key={role} className="text-sm">
                                {getEnumTranslationByKey(
                                    CaregiverRoleEnum,
                                    role,
                                )}
                            </li>
                        ))}
                    </ul>
                    {data.AreAdditionalServicesAvailable && (
                        <Paragraph
                            content={R.AdditionalServicesSelfPayment}
                            className="m-0 text-sm font-normal text-gray-900"
                        />
                    )}
                    {!data.AreAdditionalServicesAvailable && (
                        <Paragraph
                            content={R.AdditionalServicesNotAvailable}
                            className="m-0 text-sm font-normal text-gray-900"
                        />
                    )}
                    {data.ReferralOptions?.length > 0 && (
                        <VerticalContainer spacing="sm">
                            <h5>{R.ReferralsHeading}</h5>
                            <p className="m-0 text-sm font-normal text-gray-900">
                                {format(R.ReferralsCanBeReferredTo, data.Name)}
                            </p>
                            <ul className="list-disc pl-8 text-gray-900">
                                {data.ReferralOptions.map((option) => (
                                    <li key={option} className="text-sm">
                                        {getEnumTranslationByKey(
                                            ReferralOptionEnum,
                                            option,
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <Paragraph
                                content={
                                    data.ReferralsSentViaXMedicus
                                        ? R.ReferralsSentViaXMedicus
                                        : R.ReferralsPatientSendsCopy
                                }
                                className="m-0 text-sm font-normal text-gray-900"
                            />
                        </VerticalContainer>
                    )}
                    {data.AdditionalInfo && (
                        <VerticalContainer spacing="sm">
                            <h5>{R.AdditionalInfoHeading}</h5>
                            <Paragraph
                                content={data.AdditionalInfo}
                                className="m-0 text-sm font-normal text-gray-900"
                            />
                        </VerticalContainer>
                    )}
                </VerticalContainer>
            )}
        </Modal>
    );
};

export const getTitle = (
    data: InsurancePolicyProviderDetailsDto,
    isPending: boolean,
): React.ReactNode => {
    return isPending || !data ? <Skeleton className="h-5 w-1/2" /> : data.Name;
};
